import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import styled from 'styled-components';
import { enums, getHtmlFromMarkdown } from '../../../../utils';
import { convertAnchorTagsToRouterLinks } from '../../../../utils/html-parsing-helpers';
import { models } from '../../../../utils/cms-client';
import { getExitPromptFormList } from '../../../../utils/remote-config-manager';
import { KenticoDataContext } from '../../../../contexts/kentico-data-context';
import { Prompt } from '../../../base/prompt';
import FormContactCard from '../../form-contact-card';
import { generateSection } from '../generators';

const FormSectionStyled = styled.div`
  display: flex;
  flex-direction: row;

  @media only screen and (max-width: 1024px) {
    flex-direction: column;
  }
`;

export const FormLabel: (p: { labelMarkdown: string }) => React.ReactElement = (
  p,
) => {
  return (
    <div className="tw-mb-0">
      {convertAnchorTagsToRouterLinks(
        getHtmlFromMarkdown(p.labelMarkdown).__html,
      )}
    </div>
  );
};

const getPromptData = async (
  setExitReasons: Function,
  setReasonUseTextBox: Function,
  setReasonMandatory: Function,
  setIntakeList: Function,
  config: any,
) => {
  const showExitPromptList = await getExitPromptFormList();

  if (config) {
    const reasons =
      config?.[enums.RemoteConfigKeys.intakeExitReasons]?.split?.('|') ?? [];
    setExitReasons(reasons);
    //if one reason has a star at the end, it will be used to denote that this is the reason to trigger a free form text box on
    let reasonUseTextBox;
    reasons.forEach((r: string, i: number) => {
      if (r.endsWith('*')) {
        reasons[i] = r.replace(/\*/g, '');
        reasonUseTextBox = reasons[i];
      }
    });
    setReasonUseTextBox(reasonUseTextBox);
    setReasonMandatory(
      config?.[enums.RemoteConfigKeys.mandatoryExitReason] === 'true',
    );
    setIntakeList(showExitPromptList);
  }
};

export interface FormStepProps {
  formSteps: models.FormStep[];
  currentStepIndex: number;
  formStepIndex: number;
  formRenderProps: FormRenderProps;
  form: any;
  moveToStepCall: Function;
  formName: string;
  sectionName?: string;
}

const FormStep = (p: FormStepProps) => {
  const {
    formSteps,
    currentStepIndex,
    formStepIndex,
    formRenderProps,
    form,
    moveToStepCall,
    formName,
    sectionName,
  } = p;

  const [exitReasons, setExitReasons] = React.useState<string[]>([]);
  const [reasonUseTextBox, setReasonUseTextBox] = React.useState<string>('');
  const [reasonMandatory, setReasonMandatory] = React.useState<boolean>(false);
  const [intakeList, setIntakeList] = React.useState<string[]>([]);
  const { config } = React.useContext(KenticoDataContext);

  React.useEffect(() => {
    getPromptData(
      setExitReasons,
      setReasonUseTextBox,
      setReasonMandatory,
      setIntakeList,
      config,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExit = () => {
    return true;
  };

  return formSteps?.length > 0 ? (
    <>
      {intakeList && intakeList.length > 0 && intakeList.includes(formName) && (
        <Prompt
          title="Before you exit..."
          when={true}
          onOK={handleExit}
          onCancel={() => false}
          okText={'Exit claim'}
          cancelText={'Continue with claim'}
          description={
            "Your progress will be saved. If you'd like to continue exiting this claim, could you please tell us why?"
          }
          options={exitReasons}
          useTextBox={reasonUseTextBox}
          mandatory={reasonMandatory}
        />
      )}
      <FormSectionStyled>
        <div className="flex-column w-100">
          {formSteps[formStepIndex]?.elements.sections?.linkedItems?.map(
            (f, i) =>
              generateSection(
                f,
                i,
                currentStepIndex,
                formRenderProps,
                form?.elements.options_template?.value ?? '',
                moveToStepCall,
                {
                  sectionFieldNamePrefix:
                    formSteps[formStepIndex]?.elements.section_field_name_prefix
                      ?.value ?? '',
                  sectionName: sectionName ?? '',
                },
              ),
          )}
        </div>
        <FormContactCard step={formSteps[currentStepIndex]} />
      </FormSectionStyled>
    </>
  ) : null;
};

export default FormStep;
